// Masterclass booking: one row per class date, with an adult and a child
// counter on each row. Only one session can be booked at a time, so the row
// the shopper is filling in drives the variant id, the cart quantity and the
// guest line item properties on the product form.
if (document.body.classList.contains('template-product')) {

  const translations = window.masterclassTranslations || {
    errorAdultsRequired: 'Please add at least one adult.',
    errorKidRatio: 'One adult is required per child.',
    errorBookerAge: 'Please confirm you are 16 or older to book.',
    errorSeats: 'Not enough seats left for this class.',
    errorSessionRequired: 'Please choose a date and add at least one guest.',
    errorRegionMismatch: 'Your shopping location is [region]. Switch to [city] to book this class.',
    updateLocation: 'Update location'
  };

  /**
  * Update the Add to Cart button state and display error message
  */
  function updateAddToCartButton(isValid, errorMessage = '', showLocationAction = false) {
    const addToCartButton = document.getElementById('AddToCart');
    const buttonTextSpan = addToCartButton?.querySelector('.single-add-to-cart-button--text');

    // The sticky bar submits the same form, so it follows the same state.
    const stickyButton = document.getElementById('AddToCartSticky');
    if (stickyButton) {
      stickyButton.disabled = !isValid;
      stickyButton.classList.toggle('disabled', !isValid);
    }

    // Get or create error message container
    let errorContainer = document.getElementById('quantity-error-message');

    if (!errorContainer) {
      errorContainer = document.createElement('div');
      errorContainer.id = 'quantity-error-message';
      errorContainer.className = 'quantity-error-message hidden';
      errorContainer.setAttribute('role', 'alert');
      errorContainer.innerHTML = `
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="9" cy="9" r="9" fill="#E93636"/>
          <path d="M9 5V9" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M9 13C9.55228 13 10 12.5523 10 12C10 11.4477 9.55228 11 9 11C8.44772 11 8 11.4477 8 12C8 12.5523 8.44772 13 9 13Z" fill="white"/>
        </svg>
        <span class="quantity-error-message__text"></span>
        <button type="button" class="quantity-error-message__action hidden"></button>
      `;

      errorContainer.querySelector('.quantity-error-message__action').addEventListener('click', () => {
        document.querySelector('.location-drawer')?.classList.add('active');
        document.body.classList.add('open-cc');
      });

      // Insert error container after the add to cart button
      addToCartButton?.parentElement?.appendChild(errorContainer);

      // Add CSS if not already added
      if (!document.getElementById('quantity-error-styles')) {
        const style = document.createElement('style');
        style.id = 'quantity-error-styles';
        style.textContent = `
          .quantity-error-message {
            display: flex;
            align-items: center;
            gap: 10px;
            color: #c22020;
            font-size: 12px;
            margin-top: 10px;
            padding: 10px;
            background-color: #fff5f5;
            border: 1px solid #ffdddd;
            border-radius: 4px;
          }

          .quantity-error-message.hidden {
            display: none;
          }

          .quantity-error-message svg {
            flex-shrink: 0;
          }

          .quantity-error-message__text {
            flex: 1;
          }

          .quantity-error-message__action {
            border: 0;
            padding: 0;
            background: none;
            color: inherit;
            font: inherit;
            font-weight: 600;
            text-decoration: underline;
            cursor: pointer;
            white-space: nowrap;
          }

          .quantity-error-message__action.hidden {
            display: none;
          }
        `;
        document.head.appendChild(style);
      }
    }

    const errorTextSpan = errorContainer.querySelector('.quantity-error-message__text');

    if (!isValid) {
      // Disable button; the label stays "Add to cart" and the error box
      // only shows when there is a message, so the pristine state stays
      // quiet.
      if (addToCartButton) {
        addToCartButton.disabled = true;
        addToCartButton.classList.add('disabled');
      }

      if (errorTextSpan) {
        errorTextSpan.textContent = errorMessage;
      }
      const actionButton = errorContainer.querySelector('.quantity-error-message__action');
      if (actionButton) {
        actionButton.textContent = translations.updateLocation;
        actionButton.classList.toggle('hidden', !showLocationAction);
      }
      errorContainer.classList.toggle('hidden', !errorMessage);
    } else {
      // Enable button and hide error
      if (addToCartButton) {
        addToCartButton.disabled = false;
        addToCartButton.classList.remove('disabled');
        addToCartButton.classList.remove('sold-out');
        if (buttonTextSpan) {
          buttonTextSpan.textContent = buttonTextSpan.getAttribute('data-label') || 'Add to cart';
        }
      }

      errorContainer.classList.add('hidden');
    }
  }

  class SessionBooking {
    constructor(container) {
      this.container = container;
      this.rows = [...container.querySelectorAll('[data-session-row]')];
      this.locations = [...container.querySelectorAll('[data-session-location]')];
      this.adultProperty = container.querySelector('[data-session-adult-property]');
      this.kidProperty = container.querySelector('[data-session-kid-property]');
      // Sits under the Add to cart button, outside the session table.
      this.consent = document.querySelector('[data-session-consent]');
      this.announcer = container.querySelector('[data-session-announce]');
      this.form = document.querySelector('product-form form');

      const dataScript = container.querySelector('[data-session-pricing]');
      this.data = dataScript ? JSON.parse(dataScript.textContent) : { maxPax: 0, prices: {} };

      this.selectedRow = null;
      this.listen();
      this.selectLocation(this.initialLocation());
    }

    initialLocation() {
      // The shopper's city wins when it has a bookable session; otherwise
      // fall through to the first location that does, so the shopper never
      // lands on a tab where everything is sold out.
      if (!this.locations.length) return null;
      const hasSeats = (location) =>
        this.rows.some((row) => row.dataset.location === location && row.dataset.available === 'true');
      const preferred = this.container.dataset.preferredLocation;
      if (preferred && this.locations.some((tab) => tab.dataset.sessionLocation === preferred) && hasSeats(preferred)) {
        return preferred;
      }
      const first = this.locations[0].dataset.sessionLocation;
      if (hasSeats(first)) return first;
      const available = this.locations.find((tab) => hasSeats(tab.dataset.sessionLocation));
      return available ? available.dataset.sessionLocation : first;
    }

    listen() {
      this.locations.forEach((tab) => {
        tab.addEventListener('click', () => this.selectLocation(tab.dataset.sessionLocation));
      });

      this.container.addEventListener('click', (event) => {
        const button = event.target.closest('[data-session-step]');
        if (!button) return;
        const input = button.parentElement.querySelector('[data-session-count]');
        this.setCount(input, this.count(input) + parseInt(button.dataset.sessionStep, 10));
      });

      this.container.addEventListener('input', (event) => {
        const input = event.target.closest('[data-session-count]');
        if (input) this.setCount(input, this.count(input));
      });

      this.consent?.addEventListener('change', () => {
        if (this.selectedRow) this.validate(this.selectedRow);
      });
    }

    count(input) {
      return Math.max(0, parseInt(input?.value, 10) || 0);
    }

    counts(row) {
      const [adults, kids] = ['adults', 'kids'].map((kind) =>
        this.count(row.querySelector(`[data-session-counter="${kind}"] [data-session-count]`))
      );
      return { adults, kids, total: adults + kids };
    }

    seats(row) {
      return parseInt(row.dataset.seats, 10);
    }

    selectLocation(location) {
      this.locations.forEach((tab) => {
        const isActive = tab.dataset.sessionLocation === location;
        tab.classList.toggle('active', isActive);
        tab.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.rows.forEach((row) => {
        row.hidden = location !== null && row.dataset.location !== location;
      });

      this.reset();
    }

    reset() {
      this.rows.forEach((row) => this.clearRow(row));
      this.selectedRow = null;
      if (this.announcer) this.announcer.textContent = '';
      this.updatePrices(0);
      updateAddToCartButton(false, '');
    }

    clearRow(row) {
      row.querySelectorAll('[data-session-count]').forEach((input) => {
        input.value = 0;
      });
      row.classList.remove('session-booking--row--selected');
      row.querySelector('[data-session-subtotal]').textContent = '';
    }

    setCount(input, value) {
      const row = input.closest('[data-session-row]');

      // Only one session can be booked at a time.
      if (this.selectedRow && this.selectedRow !== row) {
        this.clearRow(this.selectedRow);
      }
      if (this.selectedRow !== row) {
        this.refreshSeats();
      }

      const seats = this.seats(row);
      const others = this.counts(row).total - this.count(input);
      let capacity = this.data.maxPax - others;
      if (seats >= 0) {
        capacity = Math.min(capacity, seats - others);
      }

      input.value = Math.max(0, Math.min(value, capacity));

      const { adults, kids, total } = this.counts(row);
      this.selectedRow = total > 0 ? row : null;
      row.classList.toggle('session-booking--row--selected', total > 0);

      this.updateRow(row, adults, kids, total);
    }

    updateRow(row, adults, kids, total) {
      const price = this.data.prices[total];
      const subtotalCell = row.querySelector('[data-session-subtotal]');
      if (price) {
        subtotalCell.innerHTML = price.save
          ? `${price.total}<small class="session-booking--save">${price.save}</small>`
          : price.total;
      } else {
        subtotalCell.textContent = '';
      }

      // One shared live region instead of one per row, so screen readers
      // hear the new subtotal without announcing every cleared cell.
      if (this.announcer) {
        const spoken = price && total > 0 ? [price.total, price.save].filter(Boolean).join(', ') : '';
        this.announcer.textContent = spoken ? `${this.announcer.dataset.label}: ${spoken}` : '';
      }

      if (total === 0) {
        this.updateForm(row, 0, 0, 0);
        this.updatePrices(0);
        updateAddToCartButton(false, translations.errorSessionRequired);
        return;
      }

      this.updateForm(row, adults, kids, total);
      this.updatePrices(total);
      this.validate(row);
    }

    validate(row) {
      const { adults, kids } = this.counts(row);

      if (row.dataset.regionMatch === 'false') {
        const message = translations.errorRegionMismatch
          .replace('[region]', this.container.dataset.regionLabel || '')
          .replace('[city]', row.dataset.cityLabel || '');
        updateAddToCartButton(false, message, true);
        return;
      }

      if (adults < 1) {
        updateAddToCartButton(false, translations.errorAdultsRequired);
      } else if (kids > adults) {
        updateAddToCartButton(false, translations.errorKidRatio);
      } else if (this.consent && !this.consent.checked) {
        updateAddToCartButton(false, translations.errorBookerAge);
      } else {
        updateAddToCartButton(true);
      }
    }

    refreshSeats() {
      // Re-fetch the section so the seats reflect the live inventory, the
      // way the old variant picker re-rendered on every change.
      const now = Date.now();
      if (this._seatsFetchedAt && now - this._seatsFetchedAt < 10000) return;
      this._seatsFetchedAt = now;

      const sectionId = this.form?.querySelector('[name="section-id"]')?.value;
      if (!sectionId) return;

      fetch(`${window.location.pathname}?section_id=${sectionId}`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          html.querySelectorAll('[data-session-row]').forEach((freshRow) => {
            const row = this.rows.find((r) => r.dataset.variantId === freshRow.dataset.variantId);
            if (!row) return;
            row.dataset.seats = freshRow.dataset.seats;
            row.dataset.available = freshRow.dataset.available;
            row.classList.toggle('session-booking--row--unavailable', freshRow.dataset.available !== 'true');
            const seatsCell = row.querySelector('.session-booking--seats');
            const freshSeatsCell = freshRow.querySelector('.session-booking--seats');
            if (seatsCell && freshSeatsCell) seatsCell.innerHTML = freshSeatsCell.innerHTML;
          });
          // Re-clamp the current selection against the fresh seat counts.
          if (this.selectedRow) {
            const input = this.selectedRow.querySelector('[data-session-count]');
            if (input) this.setCount(input, this.count(input));
          }
        })
        .catch(() => {});
    }

    updateForm(row, adults, kids, total) {
      if (!this.form) return;

      const variantChanged = this.form.querySelector('[name="id"]')?.value !== row.dataset.variantId;
      this.form.querySelectorAll('[name="id"]').forEach((input) => {
        input.value = row.dataset.variantId;
      });
      if (variantChanged) {
        // Let the rest of the theme (analytics, listeners on the form) know
        // the variant moved, as the old variant picker did.
        this.form.querySelector('[name="id"]')?.dispatchEvent(new Event('change', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('product:variant-change', {
          detail: { variant: { id: Number(row.dataset.variantId) } }
        }));
      }

      const quantity = this.form.querySelector('[name="quantity"]');
      if (quantity) quantity.value = Math.max(total, 1);

      if (this.adultProperty) this.adultProperty.value = total ? adults : '';
      if (this.kidProperty) this.kidProperty.value = total ? kids : '';
    }

    updatePrices(total) {
      const price = this.data.prices[total] || this.data.prices[1];
      if (!price) return;

      const mainAmount = document.querySelector('.product-price-container .price ins .amount');
      if (mainAmount) mainAmount.innerHTML = price.total;

      const suffix = document.querySelector('.product-price-container [data-per-person-suffix]');
      if (suffix) {
        const label = suffix.getAttribute('data-label');
        suffix.innerHTML = total > 1 ? `&nbsp;(${price.perPerson}${label})` : label;
      }

      const buttonPrice = document.querySelector('#AddToCart .price');
      if (buttonPrice) buttonPrice.innerHTML = price.total;

      const stickyPrice = document.querySelector('.product-add-to-cart-sticky .price ins .amount');
      if (stickyPrice) stickyPrice.innerHTML = price.total;
    }
  }

  function initSessionBooking() {
    const container = document.querySelector('[data-session-booking]');
    if (container && !container.hasAttribute('data-initialised')) {
      container.setAttribute('data-initialised', 'true');
      // Lets the CSS hide the sticky bar's own variant pickers, which the
      // session table replaces.
      document.documentElement.classList.add('session-booking-active');
      new SessionBooking(container);
    }
  }

  document.addEventListener('DOMContentLoaded', initSessionBooking);

  if (document.readyState !== 'loading') {
    initSessionBooking();
  }
}
